import React from 'react';
import Icon from '../../../components/AppIcon';
import { subscriptionService } from '../../../services/subscriptionService';

const UsageAnalytics = ({ metrics, subscription, detailed = false }) => {
  if (!metrics || metrics?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Icon name="BarChart3" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Usage Data</h3>
        <p className="text-muted-foreground">
          Usage analytics will appear here once you start using the platform
        </p>
      </div>
    );
  }

  // Calculate totals and averages
  const totalUsers = metrics?.reduce((sum, m) => sum + (m?.activeUsers || 0), 0);
  const totalSessions = metrics?.reduce((sum, m) => sum + (m?.sessionsCompleted || 0), 0);
  const totalHelpers = metrics?.reduce((sum, m) => sum + (m?.helpersActive || 0), 0);
  const avgUsers = Math.round(totalUsers / metrics?.length);
  const avgSessions = Math.round(totalSessions / metrics?.length);
  const avgCostPerUser = metrics?.reduce((sum, m) => sum + (m?.costPerUser || 0), 0) / metrics?.length;

  // Get latest metrics
  const latestMetric = metrics?.[metrics?.length - 1];

  // Calculate trends (compare last 7 days vs previous 7 days)
  const recentMetrics = metrics?.slice(-7);
  const previousMetrics = metrics?.slice(-14, -7);
  
  const recentAvgUsers = recentMetrics?.reduce((sum, m) => sum + (m?.activeUsers || 0), 0) / recentMetrics?.length;
  const previousAvgUsers = previousMetrics?.reduce((sum, m) => sum + (m?.activeUsers || 0), 0) / previousMetrics?.length;
  const usersTrend = previousAvgUsers > 0 ? ((recentAvgUsers - previousAvgUsers) / previousAvgUsers) * 100 : 0;

  const recentAvgSessions = recentMetrics?.reduce((sum, m) => sum + (m?.sessionsCompleted || 0), 0) / recentMetrics?.length;
  const previousAvgSessions = previousMetrics?.reduce((sum, m) => sum + (m?.sessionsCompleted || 0), 0) / previousMetrics?.length;
  const sessionsTrend = previousAvgSessions > 0 ? ((recentAvgSessions - previousAvgSessions) / previousAvgSessions) * 100 : 0;

  const formatTrend = (trend) => {
    const isPositive = trend >= 0;
    return {
      value: `${isPositive ? '+' : ''}${trend?.toFixed(1)}%`,
      color: isPositive ? 'text-green-600' : 'text-red-600',
      icon: isPositive ? 'TrendingUp' : 'TrendingDown'
    };
  };

  const usersTrendData = formatTrend(usersTrend);
  const sessionsTrendData = formatTrend(sessionsTrend);

  return (
    <div className="space-y-6">
      {detailed && (
        <div>
          <h2 className="text-2xl font-bold mb-2">Usage Analytics</h2>
          <p className="text-muted-foreground">Track your organization's platform usage and trends</p>
        </div>
      )}
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={20} className="text-blue-600" />
            <div className={`flex items-center gap-1 text-sm ${usersTrendData?.color}`}>
              <Icon name={usersTrendData?.icon} size={14} />
              <span>{usersTrendData?.value}</span>
            </div>
          </div>
          <p className="text-2xl font-bold">{latestMetric?.activeUsers || 0}</p>
          <p className="text-sm text-muted-foreground">Active Users</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Video" size={20} className="text-green-600" />
            <div className={`flex items-center gap-1 text-sm ${sessionsTrendData?.color}`}>
              <Icon name={sessionsTrendData?.icon} size={14} />
              <span>{sessionsTrendData?.value}</span>
            </div>
          </div>
          <p className="text-2xl font-bold">{totalSessions}</p>
          <p className="text-sm text-muted-foreground">Total Sessions</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="UserCog" size={20} className="text-purple-600" />
          </div>
          <p className="text-2xl font-bold">{latestMetric?.helpersActive || 0}</p>
          <p className="text-sm text-muted-foreground">Active Helpers</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-orange-600" />
          </div>
          <p className="text-2xl font-bold">
            {subscriptionService?.formatAmount(avgCostPerUser, subscription?.plan?.currency || 'USD')}
          </p>
          <p className="text-sm text-muted-foreground">Avg Cost/User</p>
        </div>
      </div>
      {/* Usage Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4">Usage Trends (Last 30 Days)</h3>
        <div className="space-y-4">
          {/* Simple bar chart visualization */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Daily Active Users</p>
            <div className="flex items-end gap-1 h-32">
              {metrics?.slice(-30)?.map((metric, index) => {
                const maxUsers = Math.max(...metrics?.map(m => m?.activeUsers || 0));
                const height = maxUsers > 0 ? (metric?.activeUsers / maxUsers) * 100 : 0;
                return (
                  <div
                    key={index}
                    className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                    title={`${new Date(metric?.metricDate)?.toLocaleDateString()}: ${metric?.activeUsers} users`}
                  />
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Daily Sessions</p>
            <div className="flex items-end gap-1 h-32">
              {metrics?.slice(-30)?.map((metric, index) => {
                const maxSessions = Math.max(...metrics?.map(m => m?.sessionsCompleted || 0));
                const height = maxSessions > 0 ? (metric?.sessionsCompleted / maxSessions) * 100 : 0;
                return (
                  <div
                    key={index}
                    className="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                    title={`${new Date(metric?.metricDate)?.toLocaleDateString()}: ${metric?.sessionsCompleted} sessions`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Limits */}
      {subscription && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold mb-4">Current Period Usage</h3>
          <div className="space-y-4">
            {/* Seats */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">User Seats</span>
                <span className="text-muted-foreground">
                  {subscription?.seatsUsed || 0}
                  {subscription?.plan?.seatLimit !== -1 && ` / ${subscription?.plan?.seatLimit}`}
                  {subscription?.plan?.seatLimit === -1 && ' / Unlimited'}
                </span>
              </div>
              {subscription?.plan?.seatLimit !== -1 && (
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all"
                    style={{ 
                      width: `${subscriptionService?.calculateUsagePercentage(
                        subscription?.seatsUsed || 0,
                        subscription?.plan?.seatLimit || 0
                      )}%` 
                    }}
                  />
                </div>
              )}
            </div>

            {/* Sessions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Sessions</span>
                <span className="text-muted-foreground">
                  {subscription?.sessionsUsed || 0}
                  {subscription?.plan?.sessionLimit !== -1 && ` / ${subscription?.plan?.sessionLimit}`}
                  {subscription?.plan?.sessionLimit === -1 && ' / Unlimited'}
                </span>
              </div>
              {subscription?.plan?.sessionLimit !== -1 && (
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all"
                    style={{ 
                      width: `${subscriptionService?.calculateUsagePercentage(
                        subscription?.sessionsUsed || 0,
                        subscription?.plan?.sessionLimit || 0
                      )}%` 
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Summary Stats */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4">Period Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Avg Daily Users</p>
            <p className="text-xl font-bold">{avgUsers}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Avg Daily Sessions</p>
            <p className="text-xl font-bold">{avgSessions}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Helpers</p>
            <p className="text-xl font-bold">{Math.max(...metrics?.map(m => m?.helpersActive || 0))}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;